// fcm.service.ts

import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { Injector } from '@angular/core';
import { AuthService } from './pages/auth/authServices';
import { ConfigService } from './services/config.service';
@Injectable({
  providedIn: 'root'
})
export class FcmService {
  currentMessage = new BehaviorSubject<any>(null);
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private lastSentToken: string | null = null; // Track last sent token to prevent duplicates
  private isInitializing = false; // Flag to prevent duplicate initialization

  constructor(private afMessaging: AngularFireMessaging, private injector: Injector) {
    const configService = this.injector.get(ConfigService);
    if (configService.areNotificationsDisabled()) {
      return;
    }

    this.afMessaging.messages.subscribe((message) => {
      this.currentMessage.next(message);
    });

    this.listenToTokenRefresh();
    // Initialize token on service creation (for browser reload)
    this.initializeToken();
  }

  private async initializeToken() {
    // Prevent duplicate initialization
    if (this.isInitializing) {
      return;
    }
    this.isInitializing = true;

    try {
      // Wait for config and auth service to initialize
      const checkAndInitialize = async () => {
        const configService = this.injector.get(ConfigService);
        const authService = this.injector.get(AuthService);
        
        // Wait for config to be loaded
        if (!configService.isConfigLoaded()) {
          console.log("Config not loaded yet, retrying FCM token initialization...");
          setTimeout(checkAndInitialize, 500);
          return;
        }

        if (authService.getIsAuth()) {
          // User is authenticated, get and update token
          const token = await this.getToken();
          if (token && token !== this.lastSentToken) {
            this.saveTokenToServer(token);
          }
        }
        this.isInitializing = false;
      };

      // Start checking after a short delay to allow services to initialize
      setTimeout(checkAndInitialize, 1000);
    } catch (error) {
      console.error('Error initializing FCM token:', error);
      this.isInitializing = false;
    }
  }

  requestPermission(): Promise<void> {
    return this.afMessaging.requestPermission.pipe(take(1)).toPromise();
  }

  getToken(): Promise<string | null> {
    return this.afMessaging.getToken.pipe(take(1)).toPromise();
  }

  private saveTokenToServer(token: string) {
    // Prevent duplicate calls with same token
    if (token === this.lastSentToken) {
      console.log("FCM token already sent, skipping duplicate update");
      return;
    }
    
    const configService = this.injector.get(ConfigService);
    // Check if config is loaded before sending
    if (!configService.isConfigLoaded()) {
      console.log("Config not loaded yet, will retry FCM token update");
      // Retry after a short delay
      setTimeout(() => {
        if (token !== this.lastSentToken) {
          this.saveTokenToServer(token);
        }
      }, 500);
      return;
    }
    
    this.lastSentToken = token;
    const authService = this.injector.get(AuthService);
    authService.updateFcmToken(token); 
  }

  listenToTokenRefresh() {
    this.afMessaging.tokenChanges.subscribe(
      (token) => {
        this.tokenSubject.next(token);
        if (token) {
          // Only update if token actually changed
          if (token !== this.lastSentToken) {
            console.log("FCM token changed, updating server:", token);
            this.saveTokenToServer(token);
          }
        } else {
          console.warn("FCM token is null, user may have revoked permission");
          this.lastSentToken = null; // Reset when token is revoked
        }
      },
      (error) => {
        console.error('Unable to refresh token', error);
      }
    );
  }

  getCurrentToken() {
    return this.tokenSubject.asObservable();
  }
}
